/**
 * Shared styling for the "Talk to a Specialist" / "Book a Consultation Call"
 * CTA pair. Every placement (hero, pre-footer, sticky bottom bar) imports from
 * here so the buttons stay identical.
 *
 * The class strings are written out in full rather than composed from smaller
 * fragments: Tailwind scans source text for class names, so an interpolated
 * `hover:${GRADIENT}` would never make it into the generated CSS.
 *
 * The base string repeats the layout classes that `components/ui/button` adds
 * via cva (inline-flex, centering, whitespace, transition-colors) so plain
 * anchors using it render the same as a <Button>.
 */

const BASE_CLASSNAME =
  "inline-flex h-auto items-center justify-center whitespace-nowrap rounded-[40px] px-5 py-4 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm sm:text-base lg:text-[clamp(6px,1.25vw,18px)] font-normal leading-5 tracking-[0] transition-colors";

/**
 * Blue gradient fading to periwinkle, used on "Talk to a Specialist" and
 * anywhere else that needs the same fill (e.g. the FAQ "Show More" button).
 * Exported on its own so callers with a different shape/size can still pick
 * up the identical background rather than rewriting the gradient stops.
 */
export const CTA_GRADIENT_BACKGROUND_CLASSNAME =
  "bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] hover:bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)]";

const GRADIENT_CLASSNAME = `${CTA_GRADIENT_BACKGROUND_CLASSNAME} text-white`;

/**
 * "Book a Consultation Call" — inverted: white fill, blue text and border.
 * The border is a `ring` (box-shadow) rather than an actual `border`: a real
 * border adds to the box height, which would make this button 4px taller than
 * the gradient one beside it. `ring-inset` draws the same visual outline
 * without taking up layout space, so both buttons match exactly.
 */
const OUTLINE_CLASSNAME =
  "ring-2 ring-inset ring-[#0B3CFF] bg-white text-[#0B3CFF] hover:bg-white/90";

export const CONSULTATION_ACTION = "Book a Consultation Call";
export const SPECIALIST_ACTION = "Talk to a Specialist";

export const consultationCtaClassName = (action: string): string =>
  `${BASE_CLASSNAME} ${
    action === CONSULTATION_ACTION ? OUTLINE_CLASSNAME : GRADIENT_CLASSNAME
  }`;

/**
 * Same two looks, chosen by position rather than by matching literal button
 * text. `consultationCtaClassName` only works because home-v3's hero always
 * uses the exact strings "Talk to a Specialist" / "Book a Consultation
 * Call" — everywhere else (per-page CMS hero CTAs, mirrored onto
 * StickyBottomCta) the button copy is arbitrary per page, so styling has to
 * key off which slot the button fills, not what it says.
 */
export const ctaButtonClassName = (variant: "primary" | "secondary"): string =>
  `${BASE_CLASSNAME} ${variant === "primary" ? OUTLINE_CLASSNAME : GRADIENT_CLASSNAME}`;
